import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../Service/app-service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-bulk-upload',
  templateUrl: './bulk-upload.component.html',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, NgxSpinnerModule],
  styleUrls: ['./bulk-upload.component.css']
})
export class BulkUploadComponent {
  isDragOver = false;
  // selected file object
  selectedFile: File | null = null;

  // preview values shown on UI
  selectedFileName = '';
  selectedFileSize = 0;
  selectedFileRecordCount = 0;

  // history & table
  uploadHistory: any[] = [];
  filtereduser: any[] = [];
  paginateduser: any[] = [];

  // sorting & pagination
  sortColumnuser = '';
  sortDirectionuser: 'asc' | 'desc' = 'asc';
  rowsPerPage2 = 5;
  currentPage2 = 1;
  totalPages2 = 1;

  // UI state
  fileValid = false;
  validationErrors: string[] = [];
  typeOfSub: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private SpinnerService: NgxSpinnerService,
    private tosterService: ToastrService
  ) {}

  // load history on init
  ngOnInit() {
    this.route.queryParams.subscribe(params => {
    this.typeOfSub = params['type'];
  });
    this.loadHistory();
  }

  // load upload history from backend
  loadHistory() {
    this.SpinnerService.show();
    this.authService.getAllExcelResults(this.typeOfSub).subscribe({
      next: (res: any) => {
        this.uploadHistory = (res.content || []).map((x: any) => ({
          fileName: x.fileName,
          date: x.createdAt,
          transId: x.transId,
          transactionStatus: x.transactionStatus
        }));
        this.filtereduser = [...this.uploadHistory];
        this.updatePaginationuser();
        this.SpinnerService.hide();
      },
      error: () => {
        this.SpinnerService.hide();
        this.tosterService.error('Failed to load upload history');
      }
    });
  }

  // navigate back
  goBack() {
    window.history.back();
  }

  // on file input change -> validate file type, size, and record count (xlsx parsing)
  onFileSelect(event: any) {
    this.resetSelectedFileState();
    const file: File = event?.target?.files?.[0];
    if (!file) return;

    this.selectedFile = file;
    this.selectedFileName = file.name;
    this.selectedFileSize = file.size;

    // validate extension
    const allowedExt = ['xlsx', 'xls'];
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!allowedExt.includes(ext)) {
      this.fileValid = false;
      this.validationErrors = ['Invalid file type. Only .xlsx or .xls allowed'];
      this.tosterService.warning('Invalid file type. Only .xlsx or .xls allowed');
      return;
    }

    // validate size (<= 5MB)
    const maxSizeBytes = 5 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      this.fileValid = false;
      this.validationErrors = ['File size exceeds 5MB'];
      this.tosterService.warning('File size exceeds 5MB');
      return;
    }

    // parse excel to count records (client-side) using xlsx
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { defval: null });

        // count rows: treat each entry as a record; if header present, json length is records
        const recordCount = Array.isArray(json) ? json.length : 0;
        this.selectedFileRecordCount = recordCount;

        if (recordCount === 0) {
          this.fileValid = false;
          this.validationErrors = ['No records found in the file'];
          this.tosterService.warning('No records found in the file');
          return;
        }

        if (recordCount > 100) {
          this.fileValid = false;
          this.validationErrors = ['Record count exceeds 100'];
          this.tosterService.warning('Record count exceeds 100');
          return;
        }

        // all validations passed
        this.fileValid = true;
        this.validationErrors = [];
        this.tosterService.success(`File ready: ${this.selectedFileName} (${recordCount} records)`);
      } catch (err) {
        this.fileValid = false;
        this.validationErrors = ['Unable to parse Excel file'];
        this.tosterService.error('Unable to parse Excel file');
      }
    };

    reader.onerror = () => {
      this.fileValid = false;
      this.validationErrors = ['Failed to read file'];
      this.tosterService.error('Failed to read file');
    };

    reader.readAsArrayBuffer(file);
  }

  // upload after client-side validation
  uploadProcess() {
    if (!this.selectedFile) {
      this.tosterService.warning('Please select a file');
      return;
    }

    if (!this.fileValid) {
      this.tosterService.warning('File validation failed. Check errors.');
      return;
    }

    this.SpinnerService.show();
    this.authService.uploadExcelFile(this.selectedFile, this.typeOfSub).subscribe({
      next: (res: any) => {
        this.selectedFile = null;
        this.selectedFileName = '';
        this.selectedFileSize = 0;
        this.selectedFileRecordCount = 0;
        this.fileValid = false;
        this.tosterService.success(res?.message || 'File uploaded successfully');
        this.loadHistory();
        this.SpinnerService.hide();
      },
      error: (err: any) => {
        this.SpinnerService.hide();
        const msg = (err?.error?.message) ? err.error.message : 'Upload failed';
        this.tosterService.error(msg);
      }
    });
  }

  // fetch details for a transaction
viewDetails(row: any) {
  this.router.navigate(['/report-table'], { queryParams: { id: row.transId } });
}


  // download report file (blob)
  viewReport(row: any) {
    this.SpinnerService.show();
    this.authService.downloadRawUploadedFile(row.transId).subscribe({
      next: (res: Blob) => {
        const url = window.URL.createObjectURL(res);
        const a = document.createElement('a');
        a.href = url;
        a.download = row.fileName || `report-${row.transId}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        this.SpinnerService.hide();
        this.tosterService.success('Report downloaded');
      },
      error: () => {
        this.SpinnerService.hide();
        this.tosterService.error('Failed to download report');
      }
    });
  }

  // reset selected file UI state
  resetSelectedFileState() {
    this.selectedFile = null;
    this.selectedFileName = '';
    this.selectedFileSize = 0;
    this.selectedFileRecordCount = 0;
    this.fileValid = false;
    this.validationErrors = [];
  }

  // sorting helper
  sortuser(column: string): void {
    if (this.sortColumnuser === column) {
      this.sortDirectionuser = this.sortDirectionuser === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumnuser = column;
      this.sortDirectionuser = 'asc';
    }
    this.filtereduser.sort((a: any, b: any) => {
      const valA = a[column] || '';
      const valB = b[column] || '';
      return this.sortDirectionuser === 'asc'
        ? (valA > valB ? 1 : -1)
        : (valA < valB ? 1 : -1);
    });
    this.updatePaginationuser();
  }

  // pagination update
  updatePaginationuser(): void {
    this.totalPages2 = Math.ceil(this.filtereduser.length / this.rowsPerPage2) || 1;
    this.currentPage2 = Math.min(this.currentPage2, this.totalPages2);
    const startIndex = (this.currentPage2 - 1) * this.rowsPerPage2;
    const endIndex = this.currentPage2 * this.rowsPerPage2;
    this.paginateduser = this.filtereduser.slice(startIndex, endIndex);
  }

  changePage2(page: number): void {
    if (typeof page !== 'number') return;
    this.currentPage2 = page;
    this.updatePaginationuser();
  }

  getPagesuser() {
    const pages: any[] = [];
    const max = 5;
    if (this.totalPages2 <= max) {
      for (let i = 1; i <= this.totalPages2; i++) pages.push(i);
    } else {
      const half = Math.floor(max / 2);
      if (this.currentPage2 <= half + 1) {
        for (let i = 1; i <= max - 1; i++) pages.push(i);
        pages.push('...');
        pages.push(this.totalPages2);
      } else if (this.currentPage2 >= this.totalPages2 - half) {
        pages.push(1);
        pages.push('...');
        for (let i = this.totalPages2 - (max - 2); i <= this.totalPages2; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = this.currentPage2 - half + 1; i <= this.currentPage2 + half - 1; i++) pages.push(i);
        pages.push('...');
        pages.push(this.totalPages2);
      }
    }
    return pages;
  }

  isValidPage(page: number | string): page is number {
    return typeof page === 'number';
  }

  getStartIndex(): number {
    return (this.currentPage2 - 1) * this.rowsPerPage2 + 1;
  }

  getEndIndex(): number {
    const end = this.currentPage2 * this.rowsPerPage2;
    return end > this.filtereduser.length ? this.filtereduser.length : end;
  }

  deleteRow(row: any) {
    this.uploadHistory = this.uploadHistory.filter(x => x !== row);
    this.filtereduser = [...this.uploadHistory];
    this.updatePaginationuser();
    this.tosterService.success('Deleted locally');
  }

  // download format guide
  downloadFormatGuide() {
    this.SpinnerService.show();
    this.authService.downloadSampleExcel(this.typeOfSub).subscribe({
      next: (res) => {
        const url = window.URL.createObjectURL(res);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'FormatGuide.xlsx';
        a.click();
        this.SpinnerService.hide();
        this.tosterService.success('Format guide downloaded');
      },
      error: () => {
        this.SpinnerService.hide();
        this.tosterService.error('Failed to download format guide');
      }
    });
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;

    if (!event.dataTransfer?.files || event.dataTransfer.files.length === 0) return;

    const file = event.dataTransfer.files[0];
    this.onFileSelect({ target: { files: [file] } });
  }

}
